// src/AppRoutes.jsx
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import PrivateRoute from './PrivateRoute';
import { AuthProvider, useAuth } from '../Auth/AuthContext';
import { LeadProvider } from '../contexts/LeadContext';

// Lazy load components with retry mechanism
const lazyWithRetry = (componentImport) => {
  return lazy(() => {
    return new Promise((resolve) => {
      // Add a small delay to ensure proper loading
      setTimeout(() => {
        componentImport()
          .then(resolve)
          .catch((error) => {
            console.error('Error loading component:', error);
            // Retry once after a delay
            setTimeout(() => {
              componentImport().then(resolve).catch(() => {
                // If still fails, reload the page
                window.location.reload();
              });
            }, 1000);
          });
      }, 100);
    });
  });
};

// Lazy load components with retry mechanism
const LazyHomePage = lazyWithRetry(() => import('../app/page').then(module => ({ default: module.default || module.HomePage || module })));
const LazyIntegrationPage = lazyWithRetry(() => import('../app/integrations/page').then(module => ({ default: module.default || module.IntegrationPage || module })));
const LazyInvestorPage = lazyWithRetry(() => import('../app/investors/InvestorManagement').then(module => ({ default: module.default || module.IntegrationPage || module })));
const LazySearchConsolePage = lazyWithRetry(() => import('../app/searchConsole/SearchConsole').then(module => ({ default: module.default || module.SearchConsolePage || module })));
const LazySubscriptionDetailPage = lazyWithRetry(() => import('../app/subscription-detail/page').then(module => ({ default: module.default || module.SubscriptionDetailPage || module })));
const LazyContactDatabasePage = lazyWithRetry(() => import('../app/agent/contacts/database/page').then(module => ({ default: module.default || module.ContactDatabasePage || module })));
const LazyCompanyPage = lazyWithRetry(() => import('../app/company/page').then(module => ({ default: module.default || module.CompanyPage || module })));
const LazyStakeholderMappingPage = lazyWithRetry(() => import('../app/agent/contacts/stakeholders/page').then(module => ({ default: module.default || module.StakeholderMappingPage || module })));
const LazyVendorManagementPage = lazyWithRetry(() => import('../app/agent/contacts/vendors/page').then(module => ({ default: module.default || module.VendorManagementPage || module })));
const LazyContactScoringPage = lazyWithRetry(() => import('../app/agent/contacts/scoring/page').then(module => ({ default: module.default || module.ContactScoringPage || module })));
const LazyVisitorPage = lazyWithRetry(() => import('../app/visitors/page').then(module => ({ default: module.default || module.VisitorPage || module })));
const LazyJourneyBuilderPage = lazyWithRetry(() => import('../app/journey-builder/page').then(module => ({ default: module.default || module.JourneyBuilderPage || module })));
const LazyJourneyAnalyticsPage = lazyWithRetry(() => import('../app/journey-builder/JourneyAnalyticsClient').then(module => ({ default: module.default || module.JourneyAnalyticsPage || module })));
const LazyJourneyCreatePage = lazyWithRetry(() => import('../app/journey-builder/create/page').then(module => ({ default: module.default || module.JourneyCreatePage || module })));
const LazyProductsPage = lazyWithRetry(() => import('../app/products/page').then(module => ({ default: module.default || module.ProductsPage || module })));
const LazyProductsTofuPage = lazyWithRetry(() => import('../app/products/tofu/page').then(module => ({ default: module.default || module.ProductsTofuPage || module })));
const LazyProductsMofuPage = lazyWithRetry(() => import('../app/products/mofu/page').then(module => ({ default: module.default || module.ProductsMofuPage || module })));
const LazyProductsBofuPage = lazyWithRetry(() => import('../app/products/bofu/page').then(module => ({ default: module.default || module.ProductsBofuPage || module })));
const LazyProductsColdPage = lazyWithRetry(() => import('../app/products/cold/page').then(module => ({ default: module.default || module.ProductsColdPage || module })));
const LazyProductsInvalidPage = lazyWithRetry(() => import('../app/products/invalid/page').then(module => ({ default: module.default || module.ProductsInvalidPage || module })));
const LazyProspectHuntingPage = lazyWithRetry(() => import('../app/prospect-hunting/page').then(module => ({ default: module.default || module.ProspectHuntingPage || module })));
const LazyMarketeerDataPage = lazyWithRetry(() => import('../app/marketeer-data/MarketeerData').then(module => ({ default: module.default || module.marketeerDataPage || module })));
const LazyInvestorProfilePage = lazyWithRetry(() => import('../components/investorProfile/InvestorProfile').then(module => ({ default: module.default || module.InvestorProfilePage || module })));
const LazySEOPage = lazyWithRetry(() => import('../app/seo/page').then(module => ({ default: module.default || module.SEOPage || module })));
const LazyCompetitorsPage = lazyWithRetry(() => import('../app/seo/competitors/page').then(module => ({ default: module.default || module.CompetitorsPage || module })));
const LazyAutomationPage = lazyWithRetry(() => import('../app/automation/page').then(module => ({ default: module.default || module.AutomationPage || module })));
const LazyLiveEventPage = lazyWithRetry(() => import('../components/live-events/LiveEventsComponent').then(module => ({ default: module.default || module.LiveEventsComponent || module })));
const LazySubscriptionListPage = lazyWithRetry(() => import('../app/subscription-list/page').then(module => ({ default: module.default || module.SubscriptionListPage || module })));
const LazySegmentPage = lazyWithRetry(() => import('../app/segment/page').then(module => ({ default: module.default || module.SegmentPage || module })));
const LazyWhatsappCampaignListPage = lazyWithRetry(() => import('../app/whatsApp-campaign-list/page').then(module => ({ default: module.default || module.WhatsappCampaignListPage || module })));
const LazyWhatsappCampaigntPage = lazyWithRetry(() => import('../app/campaigns/create/whatsapp/page').then(module => ({ default: module.default || module.WhatsappCampaignPage || module })));
const LazysmsCampaignListPage = lazyWithRetry(() => import('../app/sms-campaign-list/page').then(module => ({ default: module.default || module.SmsCampaignListPage || module })));
const LazysmsCampaignPage = lazyWithRetry(() => import('../app/campaigns/create/sms/page').then(module => ({ default: module.default || module.SmsCampaignPage || module })));
const LazyEmailCampaignListPage = lazyWithRetry(() => import('../app/emailCampaign/page').then(module => ({ default: module.default || module.EmailCampaignPage || module })));
const LazyEmailCampaignPage = lazyWithRetry(() => import('../app/campaigns/create/email/page').then(module => ({ default: module.default || module.EmailCampaignPage || module })));
const LazysmsListPage = lazyWithRetry(() => import('../app/sms-list/page').then(module => ({ default: module.default || module.SmsListPage || module })));
const LazysmsCreatePage = lazyWithRetry(() => import('../app/sms-create/page').then(module => ({ default: module.default || module.SmsCreatePage || module })));
const LazysmsEditPage = lazyWithRetry(() => import('../app/sms-edit/page').then(module => ({ default: module.default || module.SmsEditPage || module })));
const LazyEmailListPage = lazyWithRetry(() => import('../app/email-list/page').then(module => ({ default: module.default || module.EmailListPage || module })));
const LazyEmailCampaignDetailsPage = lazyWithRetry(() => import('../app/emailCampaignDetails/page').then(module => ({ default: module.default || module.EmailCampaignDetailsPage || module })));
const LazyCampaignEmailTemplatePage = lazyWithRetry(() => import('../app/campaign-email-template/page').then(module => ({ default: module.default || module.CampaignEmailTemplatePage || module })));
const LazyEmailEditPage = lazyWithRetry(() => import('../app/email-edit/page').then(module => ({ default: module.default || module.EmailEditPage || module })));
const LazyCampaignPage = lazyWithRetry(() => import('../app/engagement/page').then(module => ({ default: module.default || module.EngagementPage || module })));
const LazyCustomersPage = lazyWithRetry(() => import('../app/customers/page').then(module => ({ default: module.default || module.CustomersPage || module })));
const LazyAdminAgentPage = lazyWithRetry(() => import('../app/admin/agent/page').then(module => ({ default: module.default || module.AgentPage || module })));
const LazyAiCampaignPage = lazyWithRetry(() => import('../components/ai-campaign/AICampaignCreator').then(module => ({ default: module.default || module.AICampaignCreator || module })));
const LazyDataServicesPage = lazyWithRetry(() => import('../app/data-services/page').then(module => ({ default: module.default || module.DataServicesPage || module })));
const LazyDataServicesUploadPage = lazyWithRetry(() => import('../app/data-services/upload/page').then(module => ({ default: module.default || module.DataServicesUploadPage || module })));
const LazyDataServicesCloudStoragePage = lazyWithRetry(() => import('../app/data-services/cloud-storage/page').then(module => ({ default: module.default || module.DataServicesCloudStoragePage || module })));
const LazyDataServicesCreatePage = lazyWithRetry(() => import('../app/data-services/create-user/page').then(module => ({ default: module.default || module.DataServicesCreatePage || module })));
const LazyDataServicesQueryPage = lazyWithRetry(() => import('../app/data-services/query/page').then(module => ({ default: module.default || module.DataServicesQueryPage || module })));
const LazyMainLayout = lazyWithRetry(() => import('../components/layout/MainLayout').then(module => ({ default: module.default || module.MainLayout || module })));
const LazySignin = lazyWithRetry(() => import('../components/common/Signin/Signin').then(module => ({ default: module.default || module.Signin || module })));
const LazyManagePage = lazyWithRetry(() => import('../app/manage/Manage').then(module => ({ default: module.default || module.ManagePage || module })));
const LazyAgentPOV = lazyWithRetry(() => import('../app/agent-pov/AgentPOV').then(module => ({ default: module.default || module.AgentPOV || module })));
const LazyAgentInvestor = lazyWithRetry(() => import('../app/agent-pov/InvestorModal').then(module => ({ default: module.default || module.AgentPOV || module })));
const LazyResearcherDashboard = lazyWithRetry(() => import('../app/manage/ResearcherDashboard').then(module => ({ default: module.default || module.ResearcherDashboard || module })));

// Enhanced Loading fallback component with better UX
const LoadingFallback = () => (
    <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#e85b5e] mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading Customer 360...</p>
        </div>
    </div>
);

// Component to clear URL params
const CleanSignin = () => {
    const location = useLocation();

    useEffect(() => {
        // Clear query parameters
        if (location.search) {
            window.history.replaceState({}, document.title, '/signin');
        }
    }, [location]);

    return <LazySignin />;
};

// Root redirect component with loading state
const RootRedirect = () => {
    const { user, getFirstAvailablePath, isLoading } = useAuth();
    
    if (isLoading) {
        return <LoadingFallback />;
    }
    
    const redirectPath = getFirstAvailablePath();
    console.log('Root redirect to:', redirectPath);
    return <Navigate to={redirectPath} replace />;
};

// Catch-all redirect component with loading state
const CatchAllRedirect = () => {
    const { user, getFirstAvailablePath, isLoading } = useAuth();
    const location = useLocation();
    
    useEffect(() => {
        if (!isLoading) {
            console.log('Catch-all redirect from:', location.pathname);
        }
    }, [location, isLoading]);
    
    if (isLoading) {
        return <LoadingFallback />;
    }
    
    const redirectPath = getFirstAvailablePath();
    return <Navigate to={redirectPath} replace />;
};

// Error Boundary Component
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error('Error caught by boundary:', error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className="flex items-center justify-center h-screen bg-gray-50">
                    <div className="text-center p-8 max-w-md">
                        <h2 className="text-2xl font-bold text-red-600 mb-4">Something went wrong</h2>
                        <p className="text-gray-600 mb-4">We're having trouble loading this page.</p>
                        <button
                            onClick={() => window.location.reload()}
                            className="px-4 py-2 bg-[#e85b5e] text-white rounded-md hover:bg-[#d44a4d] transition-colors"
                        >
                            Refresh Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

function AppRoutes() {
    return (
        <BrowserRouter>
            <LeadProvider>
                <AuthProvider>
                    {/* Toast Container */}
                    <Toaster
                        position="top-right"
                        reverseOrder={false}
                        gutter={8}
                        toastOptions={{
                            className: '',
                            duration: 4000,
                            style: {
                                background: 'transparent',
                                color: '#fff',
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                            },
                            success: {
                                duration: 4000,
                                style: {
                                    background: '#10B981',
                                },
                            },
                            error: {
                                duration: 5000,
                                style: {
                                    background: '#EF4444',
                                },
                            },
                        }}
                    />
                    
                    <ErrorBoundary>
                        <Suspense fallback={<LoadingFallback />}>
                            <Routes>
                                {/* Public routes */}
                                <Route path="/signin" element={<CleanSignin />} />

                                {/* Protected routes with MainLayout wrapper */}
                                <Route element={<PrivateRoute />}>
                                    {/* MainLayout wrapper for all protected pages */}
                                    <Route element={<LazyMainLayout />}>
                                        {/* Dashboard */}
                                        <Route path="/dashboard" element={<LazyHomePage />} />
                                        
                                        {/* Root path - redirect based on user's accessPath */}
                                        <Route path="/" element={<RootRedirect />} />

                                        {/* All protected routes */}
                                        <Route path="/agent/contacts/database" element={<LazyContactDatabasePage />} />
                                        <Route path="/agent/contacts/stakeholders" element={<LazyStakeholderMappingPage />} />
                                        <Route path="/agent/contacts/vendors" element={<LazyVendorManagementPage />} />
                                        <Route path="/agent/contacts/scoring" element={<LazyContactScoringPage />} />
                                        <Route path="/visitors" element={<LazyVisitorPage />} />
                                        <Route path="/investor" element={<LazyInvestorPage />} />
                                        <Route path="/journey-builder" element={<LazyJourneyBuilderPage />} />
                                        <Route path="/journey-builder/create" element={<LazyJourneyCreatePage />} />
                                        <Route path="/journey-builder/analytics/:id" element={<LazyJourneyAnalyticsPage />} />
                                        <Route path="/agent/contacts/company" element={<LazyCompanyPage />} />
                                        <Route path="/automation" element={<LazyAutomationPage />} />
                                        <Route path="/products" element={<LazyProductsPage />} />
                                        <Route path="/products/tofu" element={<LazyProductsTofuPage />} />
                                        <Route path="/products/mofu" element={<LazyProductsMofuPage />} />
                                        <Route path="/products/bofu" element={<LazyProductsBofuPage />} />
                                        <Route path="/products/cold" element={<LazyProductsColdPage />} />
                                        <Route path="/products/invalid" element={<LazyProductsInvalidPage />} />
                                        <Route path="/prospect-hunting" element={<LazyProspectHuntingPage />} />
                                        <Route path="/marketeer-data" element={<LazyMarketeerDataPage />} />
                                        <Route path="/investors/:id" element={<LazyInvestorProfilePage />} />
                                        <Route path="/seo" element={<LazySEOPage />} />
                                        <Route path="/seo/competitors" element={<LazyCompetitorsPage />} />
                                        <Route path="/admin/live-events" element={<LazyLiveEventPage />} />
                                        <Route path="/segment" element={<LazySegmentPage />} />
                                        <Route path="/whatsApp-campaign-list" element={<LazyWhatsappCampaignListPage />} />
                                        <Route path="/campaigns/create/whatsapp" element={<LazyWhatsappCampaigntPage />} />
                                        <Route path="/sms-campaign-list" element={<LazysmsCampaignListPage />} />
                                        <Route path="/campaigns/create/sms" element={<LazysmsCampaignPage />} />
                                        <Route path="/emailCampaign" element={<LazyEmailCampaignListPage />} />
                                        <Route path="/campaigns/create/email" element={<LazyEmailCampaignPage />} />
                                        <Route path="/sms-list" element={<LazysmsListPage />} />
                                        <Route path="/sms-create" element={<LazysmsCreatePage />} />
                                        <Route path="/sms-edit/:id" element={<LazysmsEditPage />} />
                                        <Route path="/email-list" element={<LazyEmailListPage />} />
                                        <Route path="/campaign-email-template" element={<LazyCampaignEmailTemplatePage />} />
                                        <Route path="/engagement" element={<LazyCampaignPage />} />
                                        <Route path="/email-edit/:id" element={<LazyEmailEditPage />} />
                                        <Route path="/emailCampaignDetails/:id" element={<LazyEmailCampaignDetailsPage />} />
                                        <Route path="/customers" element={<LazyCustomersPage />} />
                                        <Route path="/subscription-detail" element={<LazySubscriptionDetailPage />} />
                                        <Route path="/subscription-list" element={<LazySubscriptionListPage />} />
                                        <Route path="/admin/agent" element={<LazyAdminAgentPage />} />
                                        <Route path="/ai-campaign" element={<LazyAiCampaignPage />} />
                                        <Route path="/search-console" element={<LazySearchConsolePage />} />
                                        <Route path="/data-services" element={<LazyDataServicesPage />} />
                                        <Route path="/data-services/upload" element={<LazyDataServicesUploadPage />} />
                                        <Route path="/data-services/cloud-storage" element={<LazyDataServicesCloudStoragePage />} />
                                        <Route path="/data-services/query" element={<LazyDataServicesQueryPage />} />
                                        <Route path="/data-services/create-user" element={<LazyDataServicesCreatePage />} />
                                        <Route path="/integrations" element={<LazyIntegrationPage />} />
                                        <Route path="/manage" element={<LazyManagePage />} />
                                        <Route path="/manage/researcher-dashboard" element={<LazyResearcherDashboard />} />
                                        <Route path="/agent-pov" element={<LazyAgentPOV />} />
                                        <Route path="/agent-pov/details" element={<LazyAgentInvestor />} />

                                        {/* Catch-all redirect */}
                                        <Route path="*" element={<CatchAllRedirect />} />
                                    </Route>
                                </Route>

                                {/* Catch-all redirect for non-authenticated */}
                                <Route path="*" element={<Navigate to="/signin" replace />} />
                            </Routes>
                        </Suspense>
                    </ErrorBoundary>
                </AuthProvider>
            </LeadProvider>
        </BrowserRouter>
    );
}

export default AppRoutes;